import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { initialState } from './project.state';
import {
    Environment,
    EnvironmentByProject,
    Label,
    LabelByProject,
    Project,
    APP_NAME,
    ProjectNameIcon,
    DashboardProject,
    AlertService,
    PROJECT_MESSAGES,
    PUBLISH_MESSAGES,
    DEFAULT_GROUP,
    SUCCESS_MESSAGES
} from '@ffx/common';
import { tap, of, Observable, map, combineLatest, firstValueFrom } from 'rxjs';
import { computed, inject } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { ApplicationStore } from '../app-store/app.store';
import { FlagStore } from '../flag-store/flag.store';
import { Router } from '@angular/router';
import { ProjectService } from '../services/project/project.service';
import { Group } from '../interfaces/db-interfaces';
import { FlagsService } from '../services/flags/flags.service';
import { replaceLabel } from '../utils/label.functions';
import { UpdateFeatureFlexLambdaInterface } from '../interfaces/lambda-interfaces';
import { environment } from '@ffx/env';
import { HttpClient } from '@angular/common/http';

export const ProjectStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withComputed(({ selectedProjectRole }) => ({
        isOwner: computed(() => {
            return selectedProjectRole() === 'Owner';
        })
    })),
    withMethods((store) => {
        const appStore = inject(ApplicationStore);
        const flagStore = inject(FlagStore);
        const alertService = inject(AlertService);
        const router = inject(Router);
        const projectService = inject(ProjectService);
        const flagService = inject(FlagsService);
        const http = inject(HttpClient);

        return {
            clearStore(): void {
                patchState(store, {
                    ...initialState
                });
            },
            clearSelectedProjectsSettings(): void {
                const dashboardProjects = store.dashboardProjects();
                if (dashboardProjects.length > 0) {
                    patchState(store, {
                        ...initialState,
                        dashboardProjectsLoading: false,
                        dashboardProjects
                    });
                } else {
                    patchState(store, {
                        ...initialState
                    });
                }
            },
            setEnvironmentEditing(idToEdit: string): void {
                const newEnvironments = store.environmentsInGroup().map((environment) => {
                    if (environment.id === idToEdit) {
                        return {
                            name: environment.name,
                            id: environment.id,
                            cssColour: environment.cssColour,
                            isProd: environment.isProd,
                            editing: true
                        };
                    }
                    return environment;
                });
                patchState(store, {
                    environmentsInGroup: newEnvironments
                });
            },
            setGroupEditing(idToEdit: string): void {
                const selectedProject = store.selectedProject();
                if (selectedProject.groups) {
                    const newGroups = selectedProject.groups.map((group) => {
                        if (group.id === idToEdit) {
                            return {
                                name: group.name,
                                id: group.id,
                                cssColour: group.cssColour,
                                editing: true
                            };
                        }
                        return group;
                    });
                    patchState(store, {
                        selectedProject: { ...selectedProject, groups: newGroups }
                    });
                }
            },
            setLabelEditing(labelToEdit: Label): void {
                const newLabels = store.labels().map((label) => {
                    if (label.name === labelToEdit.name) {
                        return {
                            ...label,
                            editing: true
                        };
                    }
                    return label;
                });
                patchState(store, {
                    labels: newLabels
                });
            },
            resetEnvironmentEditing(): void {
                const uneditedEnvironments = store.environmentsInGroup().map((environment) => {
                    return { ...environment, editing: false };
                });
                patchState(store, {
                    environmentsInGroup: uneditedEnvironments
                });
            },
            resetGroupEditing(): void {
                const selectedProject = store.selectedProject();
                if (selectedProject.groups) {
                    const uneditedGroups = selectedProject.groups.map((group) => {
                        return { ...group, editing: false };
                    });
                    patchState(store, {
                        selectedProject: { ...selectedProject, groups: uneditedGroups }
                    });
                }
            },
            resetLabelsEditing(): void {
                const labels = store.labels() || [];
                const uneditedLabels = labels.map((label) => {
                    return { ...label, editing: false };
                });
                patchState(store, {
                    labels: uneditedLabels
                });
            },
            resetProjectName(): void {
                patchState(store, {
                    selectedProject: {
                        ...store.selectedProject(),
                        name: APP_NAME
                    }
                });
            },
            async deleteEnvironment(selectedEnvironmentToDelete: Environment): Promise<void> {
                patchState(store, { projectEnvironmentsLoading: true });
                try {
                    const allEnvironmentsToDelete: Environment[] = await firstValueFrom(
                        projectService.getEnvironmentsByName(selectedEnvironmentToDelete.name)
                    );
                    await firstValueFrom(projectService.deleteEnvironment(allEnvironmentsToDelete));
                    const allEnvironmentsToDeleteIds = allEnvironmentsToDelete.map((environment) => environment.id);
                    const environmentsWithoutDeleted = store
                        .environmentsInGroup()
                        .filter(
                            (environment) => !allEnvironmentsToDeleteIds.includes(environment.id) || !environment.id
                        );
                    patchState(store, {
                        environmentsInGroup: environmentsWithoutDeleted,
                        projectEnvironmentsLoading: false
                    });
                    const selectedEnvironment = environmentsWithoutDeleted.find(
                        (environment) => environment.id !== store.selectedProject().id
                    );
                    flagStore.setEnvironmentsAndSelectedEnvironmentId(
                        environmentsWithoutDeleted,
                        selectedEnvironment ? selectedEnvironment.id : ''
                    );
                    flagStore.setTriggerRefreshFlagsEffect();
                } catch {
                    const errorMessage = PROJECT_MESSAGES.DELETE_ENVIRONMENT_FAILED;
                    patchState(store, { errorMessage, environmentsInGroup: [], projectEnvironmentsLoading: false });
                    alertService.showNegativeAlert(errorMessage);
                }
            },
            async deleteLabel(labelToDelete: LabelByProject): Promise<void> {
                patchState(store, { projectLabelsLoading: true });
                try {
                    const newLabels = store.labels().filter((label) => label.name !== labelToDelete.name);
                    await firstValueFrom(projectService.deleteLabel(labelToDelete, newLabels));
                    patchState(store, {
                        labels: newLabels,
                        projectLabelsLoading: false
                    });
                } catch {
                    const errorMessage = PROJECT_MESSAGES.DELETE_LABEL_FAILED;
                    patchState(store, { errorMessage, projectLabelsLoading: false });
                    alertService.showNegativeAlert(errorMessage);
                }
            },
            async getDashboardProjectsByUserId(userUid: string): Promise<void> {
                if (store.dashboardProjects().length > 0) {
                    patchState(store, { dashboardProjectsLoading: false });
                    return;
                }
                patchState(store, { dashboardProjectsLoading: true });
                try {
                    const dashboardProjects = await firstValueFrom(
                        projectService.getDashboardProjectsByUserId(userUid)
                    );
                    patchState(store, {
                        dashboardProjects,
                        dashboardProjectsLoading: false
                    });
                } catch {
                    const errorMessage = PROJECT_MESSAGES.LOAD_PROJECTS_FAILED;
                    patchState(store, { errorMessage, dashboardProjectsLoading: false });
                    alertService.showNegativeAlert(errorMessage);
                }
            },
            getDashboardProjectsByUserIdObservable(userUid: string): Observable<DashboardProject[]> {
                if (store.dashboardProjects().length > 0) {
                    return of(store.dashboardProjects()); // Return early if dashboard projects are already loaded
                }

                return projectService.getDashboardProjectsByUserId(userUid).pipe(
                    tap((projects) => {
                        patchState(store, { dashboardProjects: projects });
                    })
                );
            },
            async addProject(project: Project): Promise<void> {
                patchState(store, { dashboardProjectsLoading: true });
                try {
                    // Add the project to the db
                    const projectDocId = uuidv4();
                    const userUid = appStore.user.uid();
                    const environmentIds: string[] = await firstValueFrom(
                        projectService.addProject(project, userUid, projectDocId)
                    );
                    const updatedDashboardProjects = store.dashboardProjects();
                    updatedDashboardProjects.push({
                        ...project,
                        id: projectDocId,
                        role: 'Owner'
                    });
                    patchState(store, {
                        dashboardProjects: updatedDashboardProjects
                    });

                    // Inform the user the project was created, but we still need to publish the environments
                    alertService.showPositiveAlert(
                        `'${project.name}' project created successfully. Please wait while we publish your default environments.`,
                        10000
                    );

                    // Create all the requests to publish all environments
                    const flagsAndEnvironmentRequests = environmentIds.map((environmentId) => {
                        const flagsAndEnvironment: UpdateFeatureFlexLambdaInterface = {
                            flags: {},
                            environmentId: environmentId,
                            projectId: projectDocId
                        };
                        const url = `/api/rest/${environment.updateFlagsEndpoint}`;
                        return firstValueFrom(http.post(url, flagsAndEnvironment));
                    });

                    try {
                        // Publish all environments
                        await Promise.all(flagsAndEnvironmentRequests);
                        patchState(store, {
                            dashboardProjectsLoading: false
                        });
                        alertService.showPositiveAlert(PUBLISH_MESSAGES.PUBLISH_ENVIRONMENTS_SUCCESSFUL, 10000);
                    } catch {
                        patchState(store, {
                            dashboardProjectsLoading: false
                        });
                        alertService.showNegativeAlert(PUBLISH_MESSAGES.PUBLISH_ENVIRONMENTS_FAILED, 10000);
                    }
                } catch {
                    const errorMessage = PROJECT_MESSAGES.ADD_PROJECT_FAILED;
                    patchState(store, {
                        errorMessage,
                        dashboardProjectsLoading: false
                    });
                    alertService.showNegativeAlert(errorMessage);
                }
            },
            async addEnvironment(environmentToAddNoId: EnvironmentByProject): Promise<void> {
                patchState(store, { projectEnvironmentsLoading: true });
                try {
                    const selectedProject = store.selectedProject();
                    const defaultProdEnvironment = await firstValueFrom(
                        projectService.getDefaultProductionEnvironmentId(selectedProject.id)
                    );
                    // First we need to get the default production environment - as this is the basis for all new environments
                    const defaultProdEnvironmentFlags = await firstValueFrom(
                        flagService.getFeatureFlagsByEnvironmentId(defaultProdEnvironment[0].id)
                    );

                    // Now we need to create all the requests needed to create this new environment for EACH group
                    const addEnvironmentRequests: any = selectedProject.groups.map(async (group) => {
                        const environmentToAdd = { ...environmentToAddNoId, id: uuidv4() };
                        try {
                            await firstValueFrom(
                                projectService.addEnvironmentToGroup(
                                    environmentToAdd,
                                    defaultProdEnvironmentFlags,
                                    group.id
                                )
                            );
                            // If the current group is the one we have selected, we need to update the displayed environments
                            if (group.id === store.selectedGroup().id) {
                                const environmentsInGroup = [...store.environmentsInGroup(), environmentToAdd];
                                patchState(store, {
                                    environmentsInGroup
                                });
                                flagStore.setEnvironmentsAndSelectedEnvironmentId(
                                    environmentsInGroup,
                                    environmentsInGroup[0].id
                                );
                            }
                            flagStore.setTriggerRefreshFlagsEffect();

                            // Now we need to publish these new environments (for each group)
                            const flagsObject = defaultProdEnvironmentFlags.reduce((tempObject: any, flag: any) => {
                                tempObject[flag.key] = flag.isOn;
                                return tempObject;
                            }, {});
                            const flagsAndEnvironment: UpdateFeatureFlexLambdaInterface = {
                                flags: flagsObject,
                                environmentId: environmentToAdd.id,
                                projectId: selectedProject.id
                            };
                            const url = `/api/rest/${environment.updateFlagsEndpoint}`;
                            // If publish fails, no need to inform user here -> they will get the chance to publish on the flags page
                            await firstValueFrom(http.post(url, flagsAndEnvironment));
                        } catch {
                            const errorMessage = PROJECT_MESSAGES.ADD_ENVIRONMENT_FAILED;
                            patchState(store, {
                                errorMessage,
                                projectEnvironmentsLoading: false
                            });
                            alertService.showNegativeAlert(errorMessage);
                        }
                    });
                    await Promise.all(addEnvironmentRequests);
                    patchState(store, { projectEnvironmentsLoading: false });
                } catch {
                    const errorMessage = PROJECT_MESSAGES.ADD_ENVIRONMENT_FAILED;
                    patchState(store, { errorMessage, projectEnvironmentsLoading: false });
                    alertService.showNegativeAlert(errorMessage);
                }
            },
            async addLabel(label: LabelByProject): Promise<void> {
                patchState(store, { projectLabelsLoading: true });
                const originalLabels = [...(store.labels() || [])];
                const newLabels = [...originalLabels, { ...label }];
                try {
                    await firstValueFrom(projectService.addLabel(label.projectId, newLabels));
                    patchState(store, { labels: newLabels, projectLabelsLoading: false });
                } catch {
                    const errorMessage = PROJECT_MESSAGES.ADD_LABEL_FAILED;
                    patchState(store, {
                        errorMessage,
                        labels: originalLabels,
                        projectEnvironmentsLoading: false
                    });
                    alertService.showNegativeAlert(errorMessage);
                }
            },
            async getGroupEnvironmentsAndSetSelectedGroup(group: Group): Promise<void> {
                patchState(store, { groupEnvironmentsLoading: true });
                try {
                    const environments = await firstValueFrom(projectService.getEnvironmentsByGroupId(group.id));
                    const mappedEnvironments = environments.map((environment: Environment) => ({
                        id: environment['id'],
                        name: environment['name'],
                        cssColour: environment['cssColour'],
                        isProd: environment['isProd']
                    }));
                    patchState(store, {
                        environmentsInGroup: mappedEnvironments,
                        groupEnvironmentsLoading: false,
                        selectedGroup: group
                    });
                    flagStore.setEnvironmentsAndSelectedEnvironmentId(mappedEnvironments, mappedEnvironments[0].id);
                } catch {
                    const errorMessage = PROJECT_MESSAGES.LOAD_GROUP_ENVIRONMENTS_FAILED;
                    patchState(store, {
                        errorMessage,
                        groupEnvironmentsLoading: false
                    });
                    alertService.showNegativeAlert(errorMessage);
                }
            },
            async getProjectSettings(projectId: string): Promise<void> {
                if (store.selectedProject().id && store.environmentsInGroup().length > 0) {
                    return;
                }
                patchState(store, { projectSettingsLoading: true });
                try {
                    const projectResponse = await firstValueFrom(projectService.getProject(projectId));
                    if (projectResponse.exists()) {
                        const projectData = projectResponse.data();
                        const project = { ...projectData, id: projectId };
                        const selectedDashboardProject = store
                            .dashboardProjects()
                            .find((project) => project.id === projectId);

                        const isDefaultGroup = project.groups.length === 1;
                        const selectedGroup = isDefaultGroup
                            ? project.groups[0]
                            : project.groups.find((group: Group) => group.id !== projectId);
                        const environments = await firstValueFrom(
                            projectService.getEnvironmentsByGroupId(selectedGroup.id)
                        );
                        const mappedEnvironments = environments.map((environment: Environment) => ({
                            id: environment['id'],
                            name: environment['name'],
                            cssColour: environment['cssColour'],
                            isProd: environment['isProd']
                        }));
                        patchState(store, {
                            selectedProject: project,
                            labels: project.labels,
                            environmentsInGroup: mappedEnvironments,
                            projectSettingsLoading: false,
                            selectedProjectRole: selectedDashboardProject?.role,
                            selectedGroup
                        });
                        flagStore.setEnvironmentsAndSelectedEnvironmentId(mappedEnvironments, mappedEnvironments[0].id);
                    } else {
                        throw new Error('Unable to retrieve project settings.');
                    }
                } catch {
                    const errorMessage = PROJECT_MESSAGES.LOAD_PROJECT_SETTINGS_FAILED;
                    patchState(store, {
                        errorMessage,
                        projectSettingsLoading: false
                    });
                    alertService.showNegativeAlert(errorMessage);
                }
            },
            async updateEnvironment(environmentToUpdate: Environment): Promise<void> {
                patchState(store, { projectEnvironmentsLoading: true });
                try {
                    await firstValueFrom(projectService.updateEnvironment(environmentToUpdate));
                    const environments = store.environmentsInGroup();
                    patchState(store, {
                        environmentsInGroup: environments.map((environment) =>
                            environment.id === environmentToUpdate.id ? environmentToUpdate : environment
                        ),
                        projectEnvironmentsLoading: false
                    });
                    flagStore.setTriggerRefreshFlagsEffect();
                } catch {
                    const errorMessage = PROJECT_MESSAGES.UPDATE_ENVIRONMENT_FAILED;
                    patchState(store, { errorMessage, projectEnvironmentsLoading: false });
                    alertService.showNegativeAlert(errorMessage);
                }
            },
            async updateLabel({
                origLabel,
                label
            }: {
                origLabel: LabelByProject;
                label: LabelByProject;
            }): Promise<void> {
                patchState(store, { projectLabelsLoading: true });
                try {
                    const labelToReplace = { name: origLabel.name, icon: origLabel.icon };
                    const newLabel = { name: label.name, icon: label.icon };
                    const newLabels = replaceLabel(store.labels(), labelToReplace, newLabel);
                    await firstValueFrom(
                        projectService.updateLabel(origLabel.projectId, labelToReplace, newLabel, newLabels)
                    );
                    patchState(store, {
                        labels: newLabels,
                        projectLabelsLoading: false
                    });
                } catch {
                    const errorMessage = PROJECT_MESSAGES.UPDATE_LABEL_FAILED;
                    alertService.showNegativeAlert(errorMessage);
                    // Clear the labels saved in the store and refresh the Project Settings page
                    patchState(store, { errorMessage, labels: [], projectLabelsLoading: false });
                    router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                        router.navigate([router.url]);
                    });
                }
            },
            async updateProjectNameIcon(updatedProject: ProjectNameIcon): Promise<void> {
                try {
                    await firstValueFrom(projectService.updateProjectNameIcon(updatedProject));
                    const updatedDashboardProjects = store.dashboardProjects().map((project) => {
                        if (project.id === updatedProject.projectId) {
                            return {
                                ...project,
                                name: updatedProject.name,
                                icon: updatedProject.icon
                            };
                        } else {
                            return project;
                        }
                    });
                    patchState(store, {
                        dashboardProjects: updatedDashboardProjects,
                        selectedProject: {
                            ...store.selectedProject(),
                            name: updatedProject.name,
                            icon: updatedProject.icon
                        }
                    });
                } catch {
                    const errorMessage = PROJECT_MESSAGES.UPDATE_PROJECT_ICON_FAILED;
                    patchState(store, { errorMessage });
                }
            },
            async deleteProject(projectId: string): Promise<void> {
                patchState(store, { deleteProjectLoading: true });
                try {
                    await firstValueFrom(projectService.deleteProject(projectId));

                    alertService.showPositiveAlert(`'${store.selectedProject().name}' successfully deleted.`);
                    patchState(store, {
                        ...initialState // Clear the store completely
                    });
                    router.navigate(['']);
                } catch {
                    const errorMessage = PROJECT_MESSAGES.DELETE_PROJECT_FAILED;
                    patchState(store, { errorMessage, deleteProjectLoading: false });
                    alertService.showNegativeAlert(errorMessage);
                }
            },
            async deleteGroup(groupToDelete: Group): Promise<void> {
                patchState(store, { projectGroupsLoading: true });
                const project = store.selectedProject();
                try {
                    await firstValueFrom(projectService.deleteGroup(project, groupToDelete));
                    const groups = project.groups?.filter((group) => group.name !== groupToDelete.name);
                    let noDefaultGroups = groups;
                    if (groups.length > 1) {
                        noDefaultGroups = groups.filter((group) => group.id !== project.id);
                    }
                    patchState(store, {
                        projectGroupsLoading: false,
                        selectedProject: { ...project, groups },
                        selectedGroup: noDefaultGroups[0]
                    });
                    alertService.showPositiveAlert(`Successfully deleted group.`);
                } catch {
                    const errorMessage = PROJECT_MESSAGES.DELETE_PROJECT_GROUP;
                    patchState(store, { errorMessage, projectGroupsLoading: false });
                    alertService.showNegativeAlert(errorMessage);
                }
            },
            async addGroup(groupToAdd: Group): Promise<void> {
                patchState(store, { projectGroupsLoading: true });
                try {
                    // We need to get the default production environment's flags, as this is the basis for all new environments in the new group
                    const environments = flagStore.environments();
                    const project = store.selectedProject();
                    const defaultProdEnvironment = await firstValueFrom(
                        projectService.getDefaultProductionEnvironmentId(project.id)
                    );
                    const defaultProdEnvironmentFlags = await firstValueFrom(
                        flagService.getFeatureFlagsByEnvironmentId(defaultProdEnvironment[0].id)
                    );

                    // Add the new group to the db and get all new environment ids
                    const environmentIds = await firstValueFrom(
                        projectService.addGroup(project, groupToAdd, environments, defaultProdEnvironmentFlags)
                    );
                    if (project.groups && project.groups.length > 0) {
                        project.groups.push(groupToAdd);
                    } else {
                        project.groups = [groupToAdd];
                    }
                    patchState(store, {
                        selectedProject: project,
                        selectedGroup: project.groups.filter((group) => group.id !== project.id)[0]
                    });

                    // Now we need to publish all the new environments
                    const flagsAndEnvironmentRequests = environmentIds.map(async (environmentId) => {
                        const flagsObject = defaultProdEnvironmentFlags.reduce((tempObject: any, flag: any) => {
                            tempObject[flag.key] = flag.isOn;
                            return tempObject;
                        }, {});
                        const flagsAndEnvironment: UpdateFeatureFlexLambdaInterface = {
                            flags: flagsObject,
                            environmentId: environmentId,
                            projectId: project.id
                        };
                        const url = `/api/rest/${environment.updateFlagsEndpoint}`;
                        await firstValueFrom(http.post(url, flagsAndEnvironment));
                    });
                    try {
                        await Promise.all(flagsAndEnvironmentRequests);
                    } catch {
                        // Do nothing
                    }
                    // No need to show alert here, user is able to publish on the flags page if this publish all fails
                    patchState(store, {
                        projectGroupsLoading: false
                    });
                    alertService.showPositiveAlert(SUCCESS_MESSAGES.ADD_GROUP);
                } catch {
                    const errorMessage = PROJECT_MESSAGES.ADD_PROJECT_GROUP;
                    patchState(store, { errorMessage, projectGroupsLoading: false });
                    alertService.showNegativeAlert(errorMessage);
                }
            },
            async updateGroup(groupToUpdate: Group): Promise<void> {
                patchState(store, { projectGroupsLoading: true });
                try {
                    const project = store.selectedProject();
                    const groups = project.groups?.map((projectGroup) => {
                        if (projectGroup.id === groupToUpdate.id) {
                            return groupToUpdate;
                        }
                        return projectGroup;
                    });
                    await firstValueFrom(projectService.updateGroup(project, groups ?? []));
                    let noDefaultGroups = groups;
                    if (groups.length > 1) {
                        noDefaultGroups = groups.filter((group) => group.id !== project.id);
                    }
                    patchState(store, {
                        projectGroupsLoading: false,
                        selectedProject: {
                            ...project,
                            groups
                        },
                        selectedGroup: noDefaultGroups[0]
                    });
                    alertService.showPositiveAlert(`Successfully updated group.`);
                } catch {
                    const errorMessage = PROJECT_MESSAGES.UPDATE_PROJECT_GROUP;
                    patchState(store, { errorMessage, projectGroupsLoading: false });
                    alertService.showNegativeAlert(errorMessage);
                }
            },
            async getGroupsAndEnvironmentsSummary(): Promise<void> {
                patchState(store, { setupPageLoading: true });
                try {
                    const groups = store.selectedProject().groups;
                    const environmentObservables = groups.map((group) =>
                        projectService.getEnvironmentsByGroupId(group.id).pipe(
                            map((environments) => ({
                                groupName: group.name,
                                groupCssColour: group.cssColour,
                                environments
                            }))
                        )
                    );
                    const groupEnvironmentResults = await firstValueFrom(combineLatest(environmentObservables));
                    if (groupEnvironmentResults.length > 1) {
                        patchState(store, {
                            allGroupsAndEnvironmentsSummary: groupEnvironmentResults.filter(
                                (groupEnvironmentResult) => groupEnvironmentResult.groupName !== DEFAULT_GROUP.name
                            ),
                            setupPageLoading: false
                        });
                    } else {
                        patchState(store, {
                            allGroupsAndEnvironmentsSummary: groupEnvironmentResults,
                            setupPageLoading: false
                        });
                    }
                } catch {
                    const errorMessage = PROJECT_MESSAGES.SETUP_PAGE_LOAD_FAILED;
                    patchState(store, {
                        errorMessage,
                        setupPageLoading: false
                    });
                    alertService.showNegativeAlert(errorMessage);
                }
            }
        };
    })
);
